import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  useForm,
  type DefaultValues,
  type FieldErrors,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form';
import type { ZodTypeAny } from 'zod';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { FormErrorSummary } from '@/components/forms/FormErrorSummary';
import { ConfirmDialog } from './ConfirmDialog';
import { CardBareContext } from '@/components/ui/card-bare-context';
import { sectionResolver } from '@/lib/utils/section-resolver.utils';
import { PROFILE_LAYOUT_STRINGS as S } from '@/lib/constants/profile-layout-strings';

export interface SectionEditDrawerProps<T extends FieldValues> {
  open: boolean;
  /** Called when the panel should close (after the discard check, if dirty). */
  onClose: () => void;
  title: string;
  description?: string;
  /** The module's full profile schema; only `fields` errors are reported. */
  schema: ZodTypeAny;
  fields: readonly string[];
  /**
   * Visible label per field, in on-screen order, for the summary at the top of a refused
   * Save (FR-189). A failing field with no entry is left out of the list rather than shown
   * by its raw name; the summary shows nothing when none of the failing fields has one.
   */
  fieldLabels?: Readonly<Record<string, string>>;
  /** The full profile as form values; the form is reset to it on every open. */
  defaultValues: DefaultValues<T>;
  onSubmit: (values: T) => void;
  isSaving: boolean;
  /** Inline failure message for the last save, already resolved from the API error. */
  errorMessage?: string;
  /** Extra reason to block Save, e.g. an upload still in flight. */
  saveDisabled?: boolean;
  children: (form: UseFormReturn<T>) => ReactNode;
}

/** What a refused Save scrolls to: the first invalid control or inline error, in page order. */
const FIRST_ERROR_SELECTOR = '[aria-invalid="true"], [role="alert"]';

/**
 * The errors the summary lists: only fields that have a label, each with a reason. A
 * row-level error (a repeatable list's entries) carries no message of its own, so it
 * gets the list-level one if there is one, else a generic pointer to the entries.
 * Pure.
 */
export function summaryErrorsFor(
  errors: FieldErrors,
  labels: Readonly<Record<string, string>>,
): FieldErrors {
  const listed: Record<string, { type: string; message: string }> = {};
  for (const name of Object.keys(labels)) {
    const error = errors[name] as { message?: unknown; root?: { message?: unknown } } | undefined;
    if (!error) continue;
    const own = typeof error.message === 'string' ? error.message : undefined;
    const root = typeof error.root?.message === 'string' ? error.root.message : undefined;
    listed[name] = { type: 'custom', message: own ?? root ?? S.ERROR_SUMMARY_ROW_REASON };
  }
  return listed;
}

interface SectionEditFooterProps {
  formId: string;
  onCancel: () => void;
  isSaving: boolean;
  canSave: boolean;
}

function SectionEditFooter({ formId, onCancel, isSaving, canSave }: SectionEditFooterProps) {
  return (
    <div className="flex w-full justify-end gap-3">
      <Button variant="outline" onClick={onCancel} disabled={isSaving} className="text-sm">
        {S.CANCEL}
      </Button>
      <Button
        type="submit"
        form={formId}
        variant="primary"
        disabled={!canSave || isSaving}
        className="text-sm"
      >
        {isSaving ? S.SAVING : S.SAVE}
      </Button>
    </div>
  );
}

/**
 * Side panel that edits one profile section (FR-166 to FR-168,
 * profile-redesign 03-workflows Flow 1).
 *
 * Owns its own form, seeded with the whole profile and reset on each open, so
 * a section's changes save without validating untouched sections and "discard"
 * means just this form. Closing a dirty form asks first. The parent closes it
 * on a successful save by setting `open` false, which skips the dirty check.
 */
export function SectionEditDrawer<T extends FieldValues>({
  open,
  onClose,
  title,
  description,
  schema,
  fields,
  fieldLabels,
  defaultValues,
  onSubmit,
  isSaving,
  errorMessage,
  saveDisabled = false,
  children,
}: SectionEditDrawerProps<T>) {
  const formId = useId();
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const resolver = useMemo(() => sectionResolver<T>(schema, fields), [schema, fields]);
  const form = useForm<T>({ resolver, defaultValues });
  const { isDirty, errors, submitCount } = form.formState;
  const fieldsRef = useRef<HTMLDivElement>(null);
  const errorCount = Object.keys(errors).length;
  // The title counts exactly what the list shows (labelled fields only).
  const summaryErrors = useMemo(
    () => (fieldLabels ? summaryErrorsFor(errors, fieldLabels) : {}),
    [errors, fieldLabels],
  );
  const summaryCount = Object.keys(summaryErrors).length;

  // Reset on open, not on every `defaultValues` change: a background refetch
  // while the panel is open must not wipe what the member is typing.
  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [open]);

  // A refused Save brings the first failing field into view and focuses it (FR-189): in a
  // long panel it is usually off screen, and a correct inline error nobody sees reads as a
  // dead button. Keyed on the submit count, so typing never moves the panel.
  useEffect(() => {
    if (submitCount === 0 || errorCount === 0) return;
    const first = fieldsRef.current?.querySelector<HTMLElement>(FIRST_ERROR_SELECTOR);
    first?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    first?.focus?.({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new submit moves the panel
  }, [submitCount]);

  const requestClose = () => {
    if (isDirty && !isSaving) {
      setIsDiscardOpen(true);
      return;
    }
    if (!isSaving) onClose();
  };

  const confirmDiscard = () => {
    setIsDiscardOpen(false);
    onClose();
  };

  const footer = (
    <SectionEditFooter
      formId={formId}
      onCancel={requestClose}
      isSaving={isSaving}
      canSave={isDirty && !saveDisabled}
    />
  );

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(next) => (next ? undefined : requestClose())}
        title={title}
        description={description}
        size="form"
        footer={footer}
      >
        <form
          id={formId}
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          {fieldLabels && (
            <FormErrorSummary
              errors={summaryErrors}
              labels={fieldLabels}
              title={S.ERROR_SUMMARY_TITLE(summaryCount)}
            />
          )}
          {errorMessage && (
            <p role="alert" className="bg-red/10 text-p6 text-red rounded-xl px-4 py-3">
              {errorMessage}
            </p>
          )}
          {/* Same column as the form itself, so the section's own layout is unchanged. */}
          <div ref={fieldsRef} className="flex flex-col gap-6">
            <CardBareContext.Provider value>{children(form)}</CardBareContext.Provider>
          </div>
        </form>
      </Drawer>
      <ConfirmDialog
        open={isDiscardOpen}
        onOpenChange={setIsDiscardOpen}
        title={S.DISCARD_TITLE}
        description={S.DISCARD_BODY}
        confirmLabel={S.DISCARD_CONFIRM}
        cancelLabel={S.DISCARD_KEEP}
        confirmVariant="danger"
        onConfirm={confirmDiscard}
      />
    </>
  );
}
